import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Epic } from "redux-observable";
import { of } from "rxjs";
import { catchError, filter, mergeMap, switchMap } from "rxjs/operators";
import { CustomerPortalState } from "..";
import { DayOfWeek } from "../../../../../types/HelperTypes";
import {
  IDailyContentPlaylist,
  IWeeklyScheduleItem,
  LoadingStatus,
  NavigationLevel,
} from "../../../../../types/NendaTypes";
import { organizationUnitService } from "../../../../http/organizationUnit.service";
import { companyService } from "../../../../http/companyService";
import { handleError, SetNotificationSuccess } from "./notificationReducer";
import { batchPutWeeklyScheduleForScreens } from "./screenReducer";

import { selectCurrentNavigationLevel } from "./workspaceReducer";
import { pl } from "date-fns/locale";

type Scope = NavigationLevel.COMPANY | NavigationLevel.PREMISE;
const EMPTY_ARRAY: IDailyContentPlaylist[] = [];

interface Scoped {
  scope: Scope;
  parentId: string;
}
interface FetchPlaylistsPayload extends Scoped {}
interface SetPlaylistsPayload extends Scoped {
  playlists: IDailyContentPlaylist[];
}
interface CreatePlaylistPayload extends Scoped {
  playlist: IDailyContentPlaylist;
}
interface UpdatePlaylistPayload extends Scoped {
  playlist: IDailyContentPlaylist;
}
interface DeletePlaylistPayload extends Scoped {
  playlistId: string;
}
export interface ScreenSelection {
  _id: string;
  name: string;
  groups: string[];
  premise: { id: string; name: string };
  schedule: { [key in DayOfWeek]?: string };
  selected: number[];
}
interface CreateAssignPayload extends Scoped {
  createPlaylist: IDailyContentPlaylist;
  selectedScreens: ScreenSelection[];
}

type MessageType = "warning" | "error" | "info" | "success" | null;

const serviceFor = (scope: Scope) =>
  scope === "company" ? companyService : organizationUnitService;

export interface DailyContentPlaylistState {
  companyPlaylists: IDailyContentPlaylist[];
  premisePlaylists: IDailyContentPlaylist[];
  status: LoadingStatus;
  editPlaylist: string | null;
  deletePlaylist: string | null;
  createEditPlaylistModalIsOpen: boolean;
  error: {
    message: string;
    type: MessageType;
  };
}

export const initialContentPlaylistState: DailyContentPlaylistState = {
  companyPlaylists: [],
  premisePlaylists: [],
  status: LoadingStatus.IDLE,
  editPlaylist: null,
  deletePlaylist: null,
  createEditPlaylistModalIsOpen: false,
  error: { message: "", type: null },
};

const slice = createSlice({
  name: "dailyContentPlaylist",
  initialState: initialContentPlaylistState,
  reducers: {
    getPlaylists: (state, _a: PayloadAction<FetchPlaylistsPayload>) => {
      state.status = LoadingStatus.LOADING;
    },
    setPlaylists: (state, a: PayloadAction<SetPlaylistsPayload>) => {
      const { scope, playlists } = a.payload;
      if (scope === "company") state.companyPlaylists = playlists;
      else state.premisePlaylists = playlists;
      state.status = LoadingStatus.SUCCEEDED;
    },

    createPlaylist: (state, _a: PayloadAction<CreatePlaylistPayload>) => {
      state.status = LoadingStatus.LOADING;
    },
    createPlaylistSuccess: (state, a: PayloadAction<CreatePlaylistPayload>) => {
      const { scope, playlist } = a.payload;
      state.companyPlaylists.push(playlist);
      if (scope === "premise") state.premisePlaylists.push(playlist);
      state.createEditPlaylistModalIsOpen = false;
      state.editPlaylist = null;
      state.status = LoadingStatus.SUCCEEDED;
    },

    createAndAssignPlaylist: (
      state,
      _a: PayloadAction<CreateAssignPayload>
    ) => {
      state.status = LoadingStatus.LOADING;
    },
    createAndAssignPlaylistSuccess: (
      state,
      a: PayloadAction<CreateAssignPayload>
    ) => {
      const { scope, createPlaylist } = a.payload;
      state.companyPlaylists.push(createPlaylist);
      if (scope === "premise") state.premisePlaylists.push(createPlaylist);
      state.createEditPlaylistModalIsOpen = false;
      state.editPlaylist = null;
      state.status = LoadingStatus.SUCCEEDED;
    },

    updatePlaylist: (state, _a: PayloadAction<UpdatePlaylistPayload>) => {
      state.status = LoadingStatus.LOADING;
    },
    updatePlaylistSuccess: (state, a: PayloadAction<UpdatePlaylistPayload>) => {
      const { playlist } = a.payload;
      const patch = (arr: IDailyContentPlaylist[]) =>
        arr.map((p) => (p._id === playlist._id ? playlist : p));
      state.companyPlaylists = patch(state.companyPlaylists);
      state.premisePlaylists = patch(state.premisePlaylists);
      state.createEditPlaylistModalIsOpen = false;
      state.editPlaylist = null;
      state.status = LoadingStatus.SUCCEEDED;
    },

    deletePlaylist: (state, _a: PayloadAction<DeletePlaylistPayload>) => {
      state.status = LoadingStatus.LOADING;
    },
    deletePlaylistSuccess: (state, a: PayloadAction<DeletePlaylistPayload>) => {
      const { playlistId } = a.payload;
      const filterOut = (arr: IDailyContentPlaylist[]) =>
        arr.filter((p) => p._id !== playlistId);
      state.companyPlaylists = filterOut(state.companyPlaylists);
      state.premisePlaylists = filterOut(state.premisePlaylists);
      state.deletePlaylist = null;
      state.status = LoadingStatus.SUCCEEDED;
    },

    setStatus: (s, a: PayloadAction<LoadingStatus>) => {
      s.status = a.payload;
    },
    setEditPlaylist: (s, a: PayloadAction<string | null>) => {
      s.editPlaylist = a.payload;
    },
    setDeletePlaylist: (s, a: PayloadAction<string | null>) => {
      s.deletePlaylist = a.payload;
    },
    setCreateEditPlaylistModalIsOpen: (s, a: PayloadAction<boolean>) => {
      s.createEditPlaylistModalIsOpen = a.payload;
    },
    setError: (
      s,
      a: PayloadAction<{
        message: string;
        type: MessageType;
      }>
    ) => {
      s.error = a.payload;
    },
  },
});

export const selectPlaylists = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.companyPlaylists;

export const selectCompanyPlaylists = createSelector(
  selectPlaylists,
  (playlists) => playlists.filter((p) => !p.premise)
);

export const selectPremisePlaylists = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.premisePlaylists;
export const selectStatus = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.status;
export const selectEditPlaylist = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.editPlaylist;
export const selectDeletePlaylist = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.deletePlaylist;
export const selectCreateEditPlaylistModalIsOpen = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.createEditPlaylistModalIsOpen;
export const selectError = (s: CustomerPortalState) =>
  s.dailyContentPlaylist.error;
export const selectPlaylistsByPremiseId = createSelector(
  selectPremisePlaylists,
  (_: CustomerPortalState, premiseId: string) => premiseId,
  (playlists, premiseId) => playlists.filter((p) => p.premise === premiseId)
);

export const selectCompanyAndPremisePlaylists = createSelector(
  selectCompanyPlaylists,
  selectPremisePlaylists,
  (companyPlaylists, premisePlaylists) => ({
    companyPlaylists,
    premisePlaylists,
  })
);

export const selectPlaylistsByScope = createSelector(
  selectCompanyAndPremisePlaylists,
  selectCurrentNavigationLevel,
  (playlists, scope) => {
    if (scope === NavigationLevel.COMPANY) {
      return {
        companyPlaylists: playlists.companyPlaylists,
        premisePlaylists: EMPTY_ARRAY,
      };
    }
    if (scope === NavigationLevel.PREMISE) {
      return playlists;
    }
    return {
      companyPlaylists: EMPTY_ARRAY,
      premisePlaylists: EMPTY_ARRAY,
    };
  }
);

//Epics
const getPlaylists$: Epic = (action$) =>
  action$.pipe(
    filter(getPlaylists.match),
    mergeMap(({ payload }) =>
      serviceFor(payload.scope)
        .getDailyContentPlaylists(payload.parentId)
        .pipe(
          mergeMap((playlists: IDailyContentPlaylist[]) => [
            setPlaylists({ ...payload, playlists }),
            setStatus(LoadingStatus.SUCCEEDED),
          ]),
          catchError((err) =>
            of(setStatus(LoadingStatus.FAILED), handleError(err))
          )
        )
    )
  );

const createPlaylist$: Epic = (action$) =>
  action$.pipe(
    filter(createPlaylist.match),
    switchMap(({ payload }) =>
      serviceFor(payload.scope)
        .createDailyContentPlaylist(payload.parentId, payload.playlist)
        .pipe(
          mergeMap((pl) => [
            slice.actions.createPlaylistSuccess({ ...payload, playlist: pl }),
            setCreateEditPlaylistModalIsOpen(false),
            setEditPlaylist(null),
            SetNotificationSuccess("Playlist created successfully"),
          ]),
          catchError((err) => of(handleError(err)))
        )
    )
  );

const createAndAssignPlaylist$: Epic = (action$) =>
  action$.pipe(
    filter(createAndAssignPlaylist.match),
    switchMap(({ payload }) =>
      serviceFor(payload.scope)
        .createDailyContentPlaylist(payload.parentId, payload.createPlaylist)
        .pipe(
          mergeMap((pl) => {
            const items: IWeeklyScheduleItem[] = payload.selectedScreens.map(
              (s) => {
                const schedule = { ...s.schedule };
                s.selected.forEach((d) => (schedule[d] = pl._id));
                // Remove empty  days
                Object.keys(schedule).forEach(
                  (k) => !schedule[k] && delete schedule[k]
                );
                return { screenId: s._id, schedule };
              }
            );

            return [
              slice.actions.createAndAssignPlaylistSuccess({
                ...payload,
                createPlaylist: pl,
              }),
              batchPutWeeklyScheduleForScreens(items),
              setCreateEditPlaylistModalIsOpen(false),
              setEditPlaylist(null),
              SetNotificationSuccess(
                "Playlist created and assigned successfully"
              ),
            ];
          }),
          catchError((err) => of(handleError(err)))
        )
    )
  );

const updatePlaylist$: Epic = (action$) =>
  action$.pipe(
    filter(updatePlaylist.match),
    switchMap(({ payload }) =>
      serviceFor(payload.scope)
        .updateDailyContentPlaylist(payload.playlist)
        .pipe(
          mergeMap((pl) => [
            slice.actions.updatePlaylistSuccess({ ...payload, playlist: pl }),
            setCreateEditPlaylistModalIsOpen(false),
            setEditPlaylist(null),
            SetNotificationSuccess("Playlist updated successfully"),
          ]),
          catchError((err) => of(handleError(err)))
        )
    )
  );

const deletePlaylist$: Epic = (action$) =>
  action$.pipe(
    filter(deletePlaylist.match),
    switchMap(({ payload }) =>
      serviceFor(payload.scope)
        .deleteDailyContentPlaylist(payload.parentId, payload.playlistId)
        .pipe(
          mergeMap(() => [
            slice.actions.deletePlaylistSuccess(payload),
            setDeletePlaylist(null),
            SetNotificationSuccess("Playlist deleted successfully"),
          ]),
          catchError((err) => of(handleError(err)))
        )
    )
  );

export const dailyContentPlaylistEpics = [
  getPlaylists$,
  createPlaylist$,
  createAndAssignPlaylist$,
  updatePlaylist$,
  deletePlaylist$,
];

export const {
  getPlaylists,
  setPlaylists,
  setStatus,
  setEditPlaylist,
  setDeletePlaylist,
  deletePlaylist,
  setCreateEditPlaylistModalIsOpen,
  updatePlaylist,
  createPlaylist,
  setError,
  createAndAssignPlaylist,
} = slice.actions;

export default slice.reducer;
